import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Box,
  useTheme,
} from "@mui/material";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { getStats } from "../../context/Stats";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const Trends = () => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getStats()
      .then((response) => {
        setStats(response);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  }, []);

  // Dark theme chart options
  const getChartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: "bold",
        },
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function (tooltipItem) {
            return tooltipItem.raw;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.divider,
        },
      },
    },
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 12 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Failed to load data: {error.message}
        </Alert>
      </Container>
    );
  }

  // Chart data configurations with dark theme support
  const userVerificationData = {
    labels: ["Verified", "Pending", "Rejected", "Blocked"],
    datasets: [
      {
        label: "User Verification Status",
        data: [
          stats.totalUsers -
            (stats.pendingVerification +
              stats.rejectedUsers +
              stats.blockedUsers), // Verified
          stats.pendingVerification,
          stats.rejectedUsers,
          stats.blockedUsers,
        ],
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.text.disabled,
          theme.palette.error.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  const monthlySignupsData = {
    labels: stats.usersByMonth.map(({ year, month }) => `${month}/${year}`),
    datasets: [
      {
        label: "Users Joined By Month",
        data: stats.usersByMonth.map(({ count }) => count),
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: theme.palette.background.paper,
        pointBorderColor: theme.palette.primary.main,
        pointBorderWidth: 2,
      },
    ],
  };

  const userStatsBarData = {
    labels: ["Total Users", "Active", "Disabled", "Free Tier", "Gold Tier"],
    datasets: [
      {
        label: "User Stats",
        data: [
          stats.totalUsers,
          stats.activeUsers,
          stats.disabledUsers,
          stats.freeTierUsers,
          stats.goldTierUsers,
        ],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.success.main,
          theme.palette.error.main,
          theme.palette.info.main,
          theme.palette.warning.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  const monthlyVsYearlySignupsData = {
    labels: ["Last Month", "This Year"],
    datasets: [
      {
        label: "Signups Comparison",
        data: [stats.registeredLastMonth, stats.registeredThisYear],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.success.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  const userAccountsVsKhatunaData = {
    labels: ["User Accounts", "Khatuna"],
    datasets: [
      {
        label: "Accounts vs Khatuna",
        data: [stats.totalUserAccounts, stats.totalKhatuna],
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
        ],
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: "bold",
          mb: 4,
          color: theme.palette.text.primary,
        }}
      >
        Dashboard Analytics
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          {
            label: "Total Users",
            value: stats.totalUsers,
            color: "primary.main",
          },
          {
            label: "Active Users",
            value: stats.activeUsers,
            color: "success.main",
          },
          {
            label: "Blocked Users",
            value: stats.blockedUsers,
            color: "error.main",
          },
          {
            label: "Disabled Users",
            value: stats.disabledUsers,
            color: "warning.main",
          },
          {
            label: "Email Verified",
            value: stats.emailVerifiedUsers,
            color: "info.main",
          },
          {
            label: "Pending Verification",
            value: stats.pendingVerification,
            color: "orange",
          },
        ].map((item) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={item.label}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                boxShadow: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: item.color, mt: 1 }}
              >
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Line Chart for Monthly User Signups */}
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Monthly User Signups
            </Typography>
            <Box sx={{ flex: 1, minHeight: "300px" }}>
              <Line data={monthlySignupsData} options={getChartOptions()} />
            </Box>
          </Paper>
        </Grid>

        {/* Bar Chart for Total User Stats */}
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              User Statistics
            </Typography>
            <Box sx={{ flex: 1, minHeight: "300px" }}>
              <Bar data={userStatsBarData} options={getChartOptions()} />
            </Box>
          </Paper>
        </Grid>

        {/* Doughnut Chart for User Verification Status */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Verification Status
            </Typography>
            <Box sx={{ flex: 1, minHeight: "250px" }}>
              <Doughnut
                data={userVerificationData}
                options={getChartOptions()}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Bar Chart for User Accounts vs Khatuna */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Accounts vs Khatuna
            </Typography>
            <Box sx={{ flex: 1, minHeight: "250px" }}>
              <Bar
                data={userAccountsVsKhatunaData}
                options={getChartOptions()}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Doughnut Chart for Signups Last Month vs This Year */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ color: theme.palette.text.primary }}
            >
              Signups Comparison
            </Typography>
            <Box sx={{ flex: 1, minHeight: "250px" }}>
              <Doughnut
                data={monthlyVsYearlySignupsData}
                options={getChartOptions()}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Trends;
